import React, { useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { reportInvalidField } from '../lib/fieldValidity.js';

// A read-mostly group of label / value rows (one record, several fields).
// Hovering the group reveals a pencil; clicking it swaps this group's values
// for inputs *in place* — same labels, same order, same grid — with a
// Cancel / Save bar. Fields without an `edit` renderer stay read-only even in
// edit mode (derived values like Spent / Remaining).
//
//   fields: [{ key, label, read, edit? }]
//     read       node shown in read mode (and for read-only fields in edit mode)
//     edit(form, set)  node shown in edit mode; omit for a read-only field
//   initialForm()  seeds the working copy when editing starts
//   onSave(form)   may throw; the message shows inline
export function EditableGroup({
  title,
  fields,
  initialForm,
  onSave,
  canEdit = true,
  // Called with true when this group enters edit mode, false when it leaves.
  // The parent uses it to keep only one group editable at a time, so two
  // groups saving from stale snapshots can't revert each other.
  onEditingChange,
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const groupRef = useRef(null);

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  function start() {
    setForm(initialForm());
    setError('');
    setEditing(true);
    onEditingChange?.(true);
  }
  function cancel() {
    setEditing(false);
    setForm(null);
    setError('');
    onEditingChange?.(false);
  }
  async function save() {
    // Save is a plain button, so a date typed wrong would otherwise save the
    // value the picker still holds and close the editor over the input.
    if (reportInvalidField(groupRef.current)) return;
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      setEditing(false);
      setForm(null);
      onEditingChange?.(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      ref={groupRef}
      className={`info-group editable-group${editing ? ' is-editing' : ''}`}
    >
      <div className="info-group-head">
        <h3>{title}</h3>
        {canEdit && !editing && (
          <button
            type="button"
            className="group-edit"
            onClick={start}
            aria-label={`Edit ${title}`}
          >
            <Pencil size={14} />
          </button>
        )}
      </div>

      {fields.map((field) => (
        <div className="info-field" key={field.key || field.label}>
          <span>{field.label}</span>
          <div>
            {editing && field.edit
              ? field.edit(form, set)
              : (field.read ?? <span className="muted-text">—</span>)}
          </div>
        </div>
      ))}

      {editing && (
        <div className="editable-group-foot">
          {error && <div className="error">{error}</div>}
          <div className="editable-group-actions">
            <button
              type="button"
              className="text-button"
              onClick={cancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary small"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
